import React, { memo } from 'react';
import { Section } from './ui/Section';
import { CheckCircle2 } from 'lucide-react';
export function SolutionSection() {
  return <>
      <Section background="night-800" className="border-t border-white/5" data-id="element-139">
        <div className="text-center mb-12" data-id="element-140">
          <span className="text-gold-500 text-sm font-bold tracking-widest uppercase mb-4 block" data-id="element-141">
            Introducing
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-142">
            The Taproot Protocol
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-143">
            A systematic method to identify the single structural root
            generating your insomnia.
          </p>
        </div>

        <div className="prose prose-lg prose-invert mx-auto text-slate-300 mb-12" data-id="element-144">
          <p className="mb-6" data-id="element-145">
            The Taproot Protocol is a systematic method that helps you identify
            the single structural root generating your insomnia and four other
            problems you've been treating separately.
          </p>

          <p className="mb-6" data-id="element-146">
            This isn't root cause analysis the way you've heard it before. Most
            "root cause" approaches stop too shallow, they find A cause and call
            it done. The Taproot Protocol keeps digging until you hit the
            structural root, the one cause branching into five problems.
          </p>

          <p className="mb-6" data-id="element-147">
            When you identify that root, you don't just understand your insomnia
            differently. You simultaneously understand:
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 list-none pl-0 mb-8" data-id="element-148">
            {['The afternoon energy crashes', 'The Sunday night anxiety', 'The dependency on coffee to function', "The irritability everyone's been tiptoeing around"].map((item, i) => <li key={i} className="flex items-center gap-3 bg-night-900 p-4 rounded border border-white/5" data-id="element-149">
                <div className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" data-id="element-150" />
                <span className="text-slate-200" data-id="element-151">{item}</span>
              </li>)}
          </ul>

          <p className="text-xl text-white font-serif italic text-center my-8" data-id="element-152">
            One root. Multiple problems connected.
          </p>

          <p className="mb-6" data-id="element-153">
            This isn't meditation. It isn't sleep hygiene. It isn't about
            "trying harder" to relax.
          </p>

          <p className="font-medium text-white" data-id="element-154">
            It's about understanding which layer you're actually stuck on, so
            you stop wasting time on solutions that were never going to work.
          </p>
        </div>
      </Section>

      <Section background="night-900" data-id="element-155">
        <div className="bg-night-950 border border-gold-500/20 rounded-xl p-8 md:p-12 shadow-2xl" data-id="element-156">
          <h3 className="font-serif text-3xl text-white mb-8 text-center" data-id="element-157">
            What You'll Hear In This Audio
          </h3>

          <div className="space-y-6" data-id="element-158">
            {["Why every solution that failed did so because it stopped exactly three layers too shallow, and how to identify which layer you're actually stuck on", 'How to recognize which of your "separate problems" are actually branches of the same hidden root (this alone will cut your problem-solving time in half)', 'Why your insomnia gets WORSE after initially improving, and what this pattern reveals about where the real root is hiding', 'The diagnostic questions that reveal which taproot category is generating your specific symptom pattern', "How to distinguish between a true root cause and a convincing decoy, so you don't waste months treating the wrong thing"].map((item, i) => <div key={i} className="flex gap-4 items-start" data-id="element-159">
                <div className="mt-1.5 w-4 h-4 rounded-full border border-gold-500 flex items-center justify-center flex-shrink-0" data-id="element-160">
                  <div className="w-2 h-2 rounded-full bg-gold-500" data-id="element-161" />
                </div>
                <p className="text-slate-300 leading-relaxed" data-id="element-162">{item}</p>
              </div>)}
          </div>

          <div className="mt-10 pt-8 border-t border-white/10 text-center" data-id="element-163">
            <p className="text-slate-400 italic" data-id="element-164">
              This isn't a step-by-step program. There's nothing to memorize.
              It's about understanding the pattern clearly enough that you stop
              firefighting symptoms and start addressing the actual structural
              cause.
            </p>
          </div>
        </div>
      </Section>
    </>;
}